/* THE VIRAL VAULT - small motion + COD helpers. No libraries.
   Loaded as a module (deferred). Everything degrades to the plain theme. */
(() => {
  const reduce = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  const finePointer = window.matchMedia('(hover: hover) and (pointer: fine)').matches;
  const isProduct = document.body.classList.contains('vlt-tpl-product');

  /* ---------- 1. Reveal on scroll (opt-in: .vlt-reveal) ---------- */
  const startReveal = () => {
    const items = document.querySelectorAll('.vlt-reveal');
    if (!items.length) return;
    if (reduce || !('IntersectionObserver' in window)) {
      items.forEach((el) => el.classList.add('is-in'));
      return;
    }
    const io = new IntersectionObserver(
      (entries) => entries.forEach((e) => {
        if (e.isIntersecting) { e.target.classList.add('is-in'); io.unobserve(e.target); }
      }),
      { rootMargin: '0px 0px -10% 0px', threshold: 0.12 }
    );
    items.forEach((el) => io.observe(el));
  };

  /* ---------- 2. Pointer tilt on product cards (desktop only) ---------- */
  const TILT = '.product-card, [class*="ai-featured-product-card-"]';
  const startTilt = () => {
    if (reduce || !finePointer) return;
    document.addEventListener('pointermove', (e) => {
      const card = e.target.closest && e.target.closest(TILT);
      if (!card) return;
      const r = card.getBoundingClientRect();
      const x = (e.clientX - r.left) / r.width - 0.5;
      const y = (e.clientY - r.top) / r.height - 0.5;
      card.style.setProperty('--vlt-ry', (x * 7).toFixed(2) + 'deg');
      card.style.setProperty('--vlt-rx', (-y * 7).toFixed(2) + 'deg');
    }, { passive: true });
    document.addEventListener('pointerout', (e) => {
      const card = e.target.closest && e.target.closest(TILT);
      if (card && !card.contains(e.relatedTarget)) {
        card.style.removeProperty('--vlt-ry');
        card.style.removeProperty('--vlt-rx');
      }
    }, { passive: true });
  };

  /* ---------- 3b. Looping animations wait while their section is off screen ---------- */
  const startPause = () => {
    if (!('IntersectionObserver' in window)) return;
    const loops = (el) => el.getAnimations({ subtree: true }).some((a) => a.effect && a.effect.getTiming().iterations === Infinity);
    const io = new IntersectionObserver((entries) => entries.forEach((e) => e.target.classList.toggle('vlt-paused', !e.isIntersecting)), { rootMargin: '200px 0px' });
    const scan = () => document.querySelectorAll('main .shopify-section, footer .shopify-section').forEach((sec) => {
      if (sec.dataset.vltLoop) return;
      if (sec.querySelector('[data-vlt-door]') || loops(sec)) { sec.dataset.vltLoop = '1'; io.observe(sec); }
    });
    scan();
    setTimeout(scan, 3000);
  };

  /* ---------- 4. EasySell COD buttons: gold metal skin ---------- */
  const COD_BG = 'linear-gradient(135deg,#f3e6bf 0%,#d9bf7f 22%,#c9a961 46%,#a88a4b 70%,#e2cd92 100%)';
  const skinCod = (btn) => {
    if (!btn) return;
    if (btn.style.getPropertyValue('background').indexOf('243, 230, 191') > -1 || btn.style.getPropertyValue('background').indexOf('#f3e6bf') > -1) return;
    const set = (k, v) => btn.style.setProperty(k, v, 'important');
    set('background', COD_BG);
    set('color', '#0a0908');
    set('border-radius', '999px');
    set('box-shadow', '0 1px 0 rgba(255,255,255,.45) inset, 0 14px 34px -12px rgba(201,169,97,.75)');
    set('border-width', '0');
    set('font-family', "'Manrope', -apple-system, sans-serif");
    set('letter-spacing', '0.06em');
    set('min-height', '56px');
    if (getComputedStyle(btn).position === 'static') set('position', 'relative');
    set('overflow', 'hidden');
    btn.querySelectorAll('span, i, .es-button-title').forEach((s) => s.style.setProperty('color', '#0a0908', 'important'));
    if (btn.closest('.quick-add, .card-gallery')) {
      // COD buttons EasySell puts over product-card photos: one compact line so the photo stays visible
      set('font-size', '11px');
      set('min-height', '32px');
      set('letter-spacing', '0.02em');
      set('padding', '7px 8px');
      set('white-space', 'nowrap');
      set('line-height', '1.2');
      const icon = btn.querySelector('.bi');
      if (icon && icon.parentElement !== btn) icon.parentElement.style.setProperty('display', 'none', 'important');
    }
    btn.classList.remove('es-animation-pulse');
    btn.classList.add('vlt-sweep', 'vlt-cod');
  };

  // EasySell renders the main COD button and its own sticky COD bar (#es-sticky).
  const codButtons = () => document.querySelectorAll('.es-popup-button, #es-sticky');

  const watched = new WeakSet();
  let queued = false;
  const pass = () => {
    queued = false;
    codButtons().forEach((btn) => {
      skinCod(btn);
      if (watched.has(btn)) return;
      watched.add(btn);
      // the app may rewrite this button's style later: put the skin back
      new MutationObserver(queue).observe(btn, { attributes: true, attributeFilter: ['style'] });
    });
  };
  function queue() { if (!queued) { queued = true; requestAnimationFrame(pass); } }

  const startCod = () => {
    pass();
    // EasySell adds its buttons late (and again in the cart drawer and sticky bar): react only when elements are added
    new MutationObserver((list) => {
      for (const m of list) for (const n of m.addedNodes) if (n.nodeType === 1) return queue();
    }).observe(document.body, { childList: true, subtree: true });
  };

  /* ---------- boot ---------- */
  const boot = () => {
    startReveal();
    startTilt();
  };

  const onLoad = (fn) => {
    if (document.readyState === 'complete') fn();
    else window.addEventListener('load', fn, { once: true });
  };

  // EasySell buttons appear on product pages, in the cart and in product cards: skin them as soon as they appear.
  startCod();
  onLoad(startPause);

  if (isProduct) {
    // Ad landing page: nothing decorative before load, then only on the first scroll or touch.
    onLoad(() => {
      const go = () => { startReveal(); window.removeEventListener('scroll', go); window.removeEventListener('touchstart', go); };
      window.addEventListener('scroll', go, { passive: true, once: true });
      window.addEventListener('touchstart', go, { passive: true, once: true });
    });
  } else {
    boot();
  }
})();
